/*
 * Bridge from an async iterable of PointValues to a Preact signal. The widget
 * layer above never touches the async iterator directly — every widget just
 * subscribes to a signal and re-renders on each tick.
 *
 * One signal per (source, pointId) pair, cached module-scope so a schema
 * screen with 40 widgets pointing at 40 leaves doesn't open 40 separate
 * subscriptions to the same underlying stream when several widgets share a
 * point (rare in practice, but the cache is nearly free).
 */

import { signal, type Signal } from '@preact/signals';
import type { IPointBindingSource, PointValue } from './binding';

interface SharedSubscription {
  readonly signal: Signal<PointValue | null>;
  refCount: number;
  abort: AbortController;
}

const cache = new WeakMap<IPointBindingSource, Map<string, SharedSubscription>>();

/** Returns a live signal for the point. Re-uses the same signal across
 *  callers subscribing to the same (source, pointId) — the underlying stream
 *  is opened lazily on first use and closed after the last unsubscribe. */
export function pointSignal(source: IPointBindingSource, pointId: string): {
  value: Signal<PointValue | null>;
  release: () => void;
} {
  let perSource = cache.get(source);
  if (!perSource) {
    perSource = new Map();
    cache.set(source, perSource);
  }

  let sub = perSource.get(pointId);
  if (!sub) {
    const sig = signal<PointValue | null>(null);
    const abort = new AbortController();
    sub = { signal: sig, refCount: 0, abort };
    perSource.set(pointId, sub);
    void pump(source, pointId, sig, abort.signal);
  }
  sub.refCount++;

  const captured = sub;
  return {
    value: captured.signal,
    release: () => {
      captured.refCount--;
      if (captured.refCount <= 0) {
        captured.abort.abort();
        perSource!.delete(pointId);
      }
    },
  };
}

async function pump(
  source: IPointBindingSource,
  pointId: string,
  target: Signal<PointValue | null>,
  abort: AbortSignal,
): Promise<void> {
  try {
    for await (const value of source.subscribe(pointId, abort)) {
      if (abort.aborted) break;
      target.value = value;
    }
  } catch (err) {
    if ((err as { name?: string }).name === 'AbortError') return;
    console.error('[pointSignal] subscription errored', { pointId, err });
  }
}
